import { useOrchidPro } from "components/Dashboard/DoctorDashboard/contexts/orchid-pro-context";
import ErrorMessage from "components/error-message";
import SuccessMessage from "components/success-message";
import ToggleSwitch from "components/toggle-switch";
import { useTimeOnPageContext } from "contexts/time-on-page";
import React, { useEffect, useMemo, useState } from "react";
import { Button, Tab, Tabs } from "react-bootstrap";
import { useNavigate, useLocation } from "react-router-dom";
import postOrchidPro from "services/postOrchidPro";
import { AmplitudePageName, track } from "utils/amplitude";
import styles from "./PatientAnalytics.module.scss";
import { useTeamContext } from "contexts/group-practice-context/TeamContext";

const DATA_ANALYSIS_OPTIONS = [
  { value: "outcome-reports", label: "Outcome Reports" },
];

const AMPLITUDE_PAGE_NAME: AmplitudePageName = "pro-settings-patient-analytics";

const PatientAnalytics = () => {
  const { setPage } = useTimeOnPageContext();
  useEffect(() => setPage(AMPLITUDE_PAGE_NAME), [setPage]);

  const [changed, setChanged] = useState(false);
  const [error, setError] = useState<any>();
  const [success, setSuccess] = useState<string>();
  const { orchidPro, setOrchidPro } = useOrchidPro();

  const navigate = useNavigate();
  const location = useLocation();
  const { activeTab } = useMemo(() => {
    const query = new URLSearchParams(location.search);
    return {
      activeTab: query.get("tab") || "",
    };
  }, [location.search]);

  const [tab, setTab] = useState(activeTab || "outcome-reports");

  const {
    enable_client_proms,
    assessment_types = [],
    frequency = "weekly",
    send_by_email = false,
    send_by_text_message = false,
  } = orchidPro?.measurement_settings || {};

  const [enable, setEnable] = useState(enable_client_proms || false);

  useEffect(() => {
    setEnable(enable_client_proms || false);
  }, [enable_client_proms]);

  const { practiceOwnerOrchidUserId } = useTeamContext();

  const saveMeasurementSettings = async () => {
    try {
      setOrchidPro(
        await postOrchidPro(
          {
            measurement_settings: {
              enable_client_proms: enable,
              assessment_types,
              frequency,
              send_by_email,
              send_by_text_message,
            },
          },
          practiceOwnerOrchidUserId,
        ),
      );
      setSuccess("Saved");
    } catch (error) {
      setError(error);
    } finally {
      setChanged(false);
    }
  };

  return (
    <div className={styles["patient-analytics"]}>
      {error && (
        <ErrorMessage
          show={true}
          onHide={() => setError(undefined)}
          errorMsg={error?.toString()}
        />
      )}
      {success && (
        <SuccessMessage
          show={true}
          onHide={() => setSuccess(undefined)}
          message={"Saved"}
          duration={1000}
        />
      )}
      <div className={styles["patient-analytics__header"]}>
        <h4 className={"rg-32 deep-blue questrial mb-0"}>Patient Analytics</h4>
        <Tabs
          activeKey={tab}
          onSelect={(k) => {
            setTab((s) => k || s);
            navigate(
              `/pro-dashboard/settings/patient-analytics?tab=${k}`,
              { replace: true }
            );
          }}
          className={styles["patient-analytics__tabs"]}
        >
          {DATA_ANALYSIS_OPTIONS.map((el, i) => {
            return (
              <Tab
                key={i}
                eventKey={el.value}
                title={el.label}
                tabClassName={styles["patient-analytics__tab"]}
              ></Tab>
            );
          })}
        </Tabs>
      </div>

      <div className={styles["patient-analytics__body"]}>
        {tab === "outcome-reports" && (
          <div>
            <div className={styles["patient-analytics__section"]}>
              <div className={styles["patient-analytics__section-title"]}>
                PROMs
              </div>
              <div className={styles["patient-analytics__content"]}>
                Orchid helps you assign, administer, score, and review clinical
                assessments, such as the PHQ-9 and GAD-7. You can easily monitor
                and engage your patients with these assessments.
              </div>
              <div className={"bd-16 my-4"}>
                Do you want to display PROMs on each patient's chart?
              </div>
              <div className={"d-flex align-items-center"}>
                <span className={"mr-3"}>Off</span>
                <span className={styles["patient-analytics__toggle-switch"]}>
                  <ToggleSwitch
                    checked={enable}
                    onChange={(e) => {
                      setChanged(true);
                      setEnable(!enable);
                    }}
                    onClick={() => {
                      track(
                        AMPLITUDE_PAGE_NAME,
                        "display-client-proms-patient-analytics-toggle",
                        "click",
                      );
                    }}
                  />
                </span>
                <span className={"ml-3"}>On</span>
              </div>
            </div>
          </div>
        )}
      </div>
      <div className={styles["patient-analytics__footer"]}>
        <div className={"gray01 rg-12"}>
          ©2026 Orchid Exchange Inc. All rights reserved
        </div>
        <Button
          variant="primary"
          onClick={() => {
            track(AMPLITUDE_PAGE_NAME, "save-button", "click");
            saveMeasurementSettings();
          }}
          className={styles["patient-analytics__save-button"]}
          disabled={!changed}
        >
          Save
        </Button>
      </div>
    </div>
  );
};

export default PatientAnalytics;
